<template>
    <div class="tab-container">
        <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <span>Loading activity history...</span>
        </div>
        <div v-else>
            <div class="activity" v-for="(activity, index) in activities" :key="activity.message_id">
                <div class="header">
                    <div class="dot">
                        <div class="dot-icon"></div>
                        <!-- hide the line in the last activity -->
                        <div v-if="index !== activities.length - 1" class="vertical-line"></div>
                    </div>
                    <div class="activity-content">
                        <div class="expo-title">
                            <span v-if="activity.message.content.message.length >= 60">
                                {{ truncateText(activity.message.content.message, 60) }}
                            </span>
                            <span v-else>
                                {{ activity.message.content.message  }}
                            </span>
                            <div class="hidden lg:block">
                                <div class="badge found min-w-fit whitespace-nowrap" v-if="activity.message.type === ConversationMessageType.SCAN_HIT">Exposure Detected</div>
                            </div>
                        </div>
                        
                        <div class="expo-time">{{ formatActivityDate(activity.timestamp_ms) }}</div>

                        <div class="flex lg:hidden">
                            <div class="badge found" v-if="activity.message.type === ConversationMessageType.SCAN_HIT">Exposures Detected</div>
                        </div>


                        <div class="pt-2" v-if="activity.scan">
                            <div class="data">
                                <p v-if="activity.scan.first_name">
                                    Name: <span>{{ activity.scan.first_name }} {{ activity.scan.last_name }}</span>
                                </p>
                                <p v-if="activity.scan.age">
                                    Age: <span>{{ activity.scan.age }}</span>
                                </p>
                                <p v-if="activity.scan.date_of_birth">
                                    Date of Birth: <span>{{ formatDate(activity.scan.date_of_birth, 'MMMM Do, YYYY') }}</span>
                                </p>
                                <div v-if="activity.scan.emails && activity.scan.emails.length > 0" class="mt-3">
                                    <p class="font-semibold text-gray-700 mb-2">Emails ({{ activity.scan.emails.length }}):</p>
                                    <div class="flex flex-wrap gap-2">
                                        <div v-for="(email, index) in activity.scan.emails" :key="index" class="flex-1 min-w-[200px] p-2 bg-gray-50 rounded">
                                            <p class="font-medium">{{ email }}</p>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="activity.scan.phones && activity.scan.phones.length > 0" class="mt-3">
                                    <p class="font-semibold text-gray-700 mb-2">Phones ({{ activity.scan.phones.length }}):</p>
                                    <div class="flex flex-wrap gap-2">
                                        <div v-for="(phone, index) in activity.scan.phones" :key="index" class="flex-1 min-w-[150px] p-2 bg-gray-50 rounded">
                                            <p class="font-medium">{{ phone }}</p>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="activity.scan.aka && activity.scan.aka.length > 0" class="mt-3">
                                    <p class="font-semibold text-gray-700 mb-2">Also Known As ({{ activity.scan.aka.length }}):</p>
                                    <div class="flex flex-wrap gap-2">
                                        <div v-for="(name, index) in activity.scan.aka" :key="index" class="flex-1 min-w-[150px] p-2 bg-gray-50 rounded">
                                            <p class="font-medium">{{ name }}</p>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="activity.scan.addresses && activity.scan.addresses.length > 0" class="mt-3">
                                    <p class="font-semibold text-gray-700 mb-2">Addresses ({{ activity.scan.addresses.length }}):</p>
                                    <div class="flex flex-wrap gap-2">
                                        <div v-for="(address, index) in activity.scan.addresses" :key="index" class="flex-1 min-w-[200px] p-2 bg-gray-50 rounded">
                                            <p v-if="address.line1" class="font-medium">{{ address.line1 }}</p>
                                            <p v-if="address.city || address.state || address.postal_code" class="text-sm text-gray-600">
                                                {{ [address.city, address.state, address.postal_code].filter(Boolean).join(', ') }}
                                            </p>
                                            <p v-if="address.country" class="text-sm text-gray-600">{{ address.country }}</p>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="activity.scan.relatives && activity.scan.relatives.length > 0" class="mt-3">
                                    <p class="font-semibold text-gray-700 mb-2">Relatives ({{ activity.scan.relatives.length }}):</p>
                                    <div class="flex flex-wrap gap-2">
                                        <div v-for="(relative, index) in activity.scan.relatives" :key="index" class="flex-1 min-w-[150px] p-2 bg-gray-50 rounded">
                                            <p class="font-medium">{{ relative.name }}</p>
                                            <p v-if="relative.age" class="text-sm text-gray-600">Age: {{ relative.age }}</p>
                                        </div>
                                    </div>
                                </div>

                                <div @click="viewImage(activity.scan.screenshot)" v-if="activity.scan.screenshot" class="pt-4 w-full cursor-pointer">
                                    <img :src="activity.scan.screenshot" alt="snapshot" class="w-full" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useDynamicDialog } from '~/composables/dynamic-dialog';
import { useDateHelpers } from '~/composables/useDateHelpers';
import type { ConversationMessage } from '~/types/companies/conversations';
import { ModalNames } from '~/types/modals';
import { ConversationMessageType } from '~/types/person/conversations';

const { truncateText } = useUtils();

defineProps<{
    activities: ConversationMessage[];
    loading: boolean;
}>();

const store = usePersonStore();
const { formatDate } = useDateHelpers();

const formatActivityDate = (timestamp_ms: number): string => {
    try {
        // Convert timestamp to string and remove last 7 characters
        const timestampStr = String(timestamp_ms);
        const truncatedTimestamp = timestampStr.slice(0, -7);
        
        // Check if the result is a valid number
        const numericTimestamp = Number(truncatedTimestamp);
        if (isNaN(numericTimestamp) || numericTimestamp <= 0) {
            return 'Invalid Date';
        }
        
        // Create date object and format
        const date = new Date(numericTimestamp * 1000); // Convert to milliseconds
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        
        return formatDate(date.toISOString(), 'MMM D, YYYY h:mma');
    } catch (error) {
        console.error('Error formatting activity date:', error);
        return 'Invalid Date';
    }
};

const viewImage = (url: string) => {
    store.imgViewerUrl = url;
    useDynamicDialog(ModalNames.IMAGE_VIEWER, {
        imageUrl: url,
        altText: 'Exposure Image',
    });
};
</script>

<style lang="scss" scoped>
.tab-container {
    @apply p-1 lg:p-4;
}

.activity {
    @apply py-0;

    .header {
        @apply flex flex-row gap-4;

        .dot {
            @apply flex flex-col items-center w-6 pt-1.5;
            height: -webkit-fill-available;

            .dot-icon {
                @apply h-2 w-2 bg-green-700 rounded-full;
            }

            .vertical-line {
                @apply w-px flex-grow bg-gray-300;
            }
        }

        .activity-content {
            @apply flex-1 flex flex-col gap-1 pb-6 lg:pb-4;
        }

        .expo-title {
            @apply flex flex-row justify-between items-center text-sm font-medium text-zinc-950 font-inter;
        }

        .expo-time {
            @apply text-xs font-normal text-gray-500 font-inter;
        }
    }
}

.data {
    p {
        @apply text-xs font-semibold text-gray-500 leading-5 font-inter;

        span {
            @apply font-normal;
        }
    }
}

.badge {
    @apply rounded-full inline-flex items-center px-2 py-0.5 text-center text-xs font-medium leading-5;

    &.found {
        @apply border border-warning-200 bg-warning-50 text-warning-700;
    }

    &.not-found {
        @apply text-gray-700 bg-white shadow-sm border border-gray-300;
    }
}

.activity-feed-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    /* Adjust as needed */
    padding: 20px;
}

.spinner {
    animation: rotate 1s linear infinite;
    width: 48px;
    height: 48px;
    display: block;
    margin: 2rem auto 1rem auto;
}

.path {
    stroke: #2563F3;
    /* Tailwind blue-600 */
    stroke-linecap: round;
    animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
    100% {
        transform: rotate(360deg);
    }
}

@keyframes dash {
    0% {
        stroke-dasharray: 1, 150;
        stroke-dashoffset: 0;
    }

    50% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -35;
    }

    100% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -124;
    }
}

.loading-container {
    @apply flex flex-col items-center justify-center py-12 gap-4;
    
    .loading-spinner {
        @apply w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin;
    }
    
    span {
        @apply text-gray-600 font-inter text-sm;
    }
}
</style>